'use client';

import { RedeemVoucherFormContent } from '@lib/types/cms/RedeemVoucherForm';
import { FormErrorMessage } from '@ui-components/components/Form/FormErrorMessage';
import { ButtonV2 } from '@ui-components/components/atoms/Button/Button.v2';
import { IconButton } from '@ui-components/components/atoms/Button/IconButton';
import { InputV2 } from '@ui-components/index';
import { StringToHtml } from '@ui-components/utils/stringToHtml';
import { c } from '@ui-components/utils/tailwind-merge';
import { DynamicIcon } from 'phosphor-icons/react';
import { FormEvent, useRef, useState } from 'react';

type RedeemVoucherProps = {
  onSubmitForm: (code: string) => void;
  isSubmitting?: boolean;
  content: Pick<
    RedeemVoucherFormContent,
    | 'heading'
    | 'input'
    | 'submitButtonLabel'
    | 'customerServiceText'
    | 'legalText'
  >;
  errorMessage?: string;
};

export const RedeemVoucherForm = ({
  onSubmitForm,
  isSubmitting = false,
  errorMessage,
  content,
}: RedeemVoucherProps) => {
  const [voucherCode, setVoucherCode] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  const handleOnSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isSubmitting) {
      onSubmitForm(voucherCode.trim());
    }
  };

  const handleOnClear = () => {
    setVoucherCode('');
    inputRef.current?.focus();
  };

  if (errorMessage) {
    inputRef.current?.setAttribute('aria-invalid', 'true');
    inputRef.current?.focus();
  } else {
    inputRef.current?.removeAttribute('aria-invalid');
  }

  return (
    <form data-test="redeem-voucher-form" noValidate onSubmit={handleOnSubmit}>
      <h2 className="header-large leading-8 md:leading-9">
        {content?.heading}
      </h2>
      <div className="my-5 flex flex-col">
        {/* Todo: Update once InputV2 is fully implemented */}
        <label className="sr-only mb-1 text-sm" htmlFor="voucher-code-input">
          {content?.input.label ?? 'Voucher Code'}
        </label>
        <div className="relative">
          <InputV2
            id="voucher-code-input"
            type="text"
            placeholder={content?.input.placeholder ?? 'Voucher code'}
            className={c('w-full pr-10', {
              ['border-2 border-red-600 hover:border-red-700']:
                Boolean(errorMessage),
            })}
            value={voucherCode}
            onChange={(e) => setVoucherCode(e.target.value)}
            ref={inputRef}
            required
          />
          {voucherCode && (
            <div className="absolute right-4 top-2 flex items-center gap-1">
              <IconButton
                iconName="X"
                ariaLabel={'Clear input'}
                iconClassName={'hover:fill-[#4268E6]'}
                onClick={handleOnClear}
              />
            </div>
          )}
        </div>
        <div aria-live="polite" id="error">
          {errorMessage && (
            <FormErrorMessage
              className="text-xs font-normal text-red-600"
              inputId="voucher-code-input"
              errorMessage={errorMessage}
            />
          )}
        </div>
      </div>
      <div className="mb-8 justify-start">
        {content?.customerServiceText && (
          <StringToHtml
            input={content.customerServiceText}
            className="text-neutral-900 text-base font-normal"
            tag="p"
          />
        )}
        {content?.legalText && (
          <StringToHtml
            input={content.legalText}
            className="text-neutral-900 text-base font-normal"
            tag="p"
          />
        )}
      </div>
      <div className="flex justify-center md:justify-start">
        <ButtonV2
          type="submit"
          label={content?.submitButtonLabel ?? 'Redeem Voucher'}
          className="inline-flex min-w-80 items-center justify-center"
        >
          {isSubmitting ? (
            <DynamicIcon
              iconName="CircleNotch"
              className="animate-spin"
              weight="bold"
              size={22}
            />
          ) : null}
        </ButtonV2>
      </div>
    </form>
  );
};
